import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  CircularProgress,
  Alert,
  Chip,
  TablePagination,
  InputAdornment,
  TextField,
} from '@mui/material';
import {
  Search as SearchIcon,
  Agriculture as AgricultureIcon,
  Pets as PetsIcon,
} from '@mui/icons-material';
import axios from 'axios';
import { API_BASE_URL } from '../config/api';

interface ProductionGuide {
  id: string;
  template_name: string;
  production_type: 'crop' | 'animal';
  variety_name?: string;
  description?: string;
  created_at: string;
  updated_at: string;
}

const ProductionGuides: React.FC = () => {
  const [guides, setGuides] = useState<ProductionGuide[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  const fetchProductionGuides = async () => {
    try {
      setLoading(true);
      const response = await axios.get(
        `${API_BASE_URL}/plan-templates`,
        {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          }
        }
      );

      if (response.data.success) {
        setGuides(response.data.data || []);
      } else {
        setError('Failed to fetch production guides');
      }
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to fetch production guides');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchProductionGuides();
  }, []);

  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(event.target.value);
    setPage(0);
  };

  const filteredGuides = guides.filter(guide =>
    guide.template_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    (guide.variety_name && guide.variety_name.toLowerCase().includes(searchTerm.toLowerCase()))
  );

  const handleChangePage = (_event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const getProductionTypeIcon = (type: string) => {
    return type === 'crop' ? <AgricultureIcon /> : <PetsIcon />;
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  return (
    <Box>
      <Typography variant="h4" gutterBottom>
        Production Guides
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      <Box sx={{ mb: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <TextField
          placeholder="Search production guides..."
          variant="outlined"
          size="small"
          value={searchTerm}
          onChange={handleSearchChange}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon />
              </InputAdornment>
            ),
          }}
          sx={{ width: 300 }}
        />
      </Box>

      {loading ? (
        <Box display="flex" justifyContent="center" p={4}>
          <CircularProgress />
        </Box>
      ) : (
        <Paper>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Guide Name</TableCell>
                  <TableCell>Type</TableCell>
                  <TableCell>Variety</TableCell>
                  <TableCell>Description</TableCell>
                  <TableCell>Created</TableCell>
                  <TableCell>Last Updated</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredGuides
                  .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                  .map((guide) => (
                    <TableRow key={guide.id}>
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          {getProductionTypeIcon(guide.production_type)}
                          {guide.template_name}
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={guide.production_type}
                          size="small"
                          color={guide.production_type === 'crop' ? 'success' : 'info'}
                        />
                      </TableCell>
                      <TableCell>{guide.variety_name || '-'}</TableCell>
                      <TableCell>
                        {guide.description
                          ? guide.description.substring(0, 100) + (guide.description.length > 100 ? '...' : '')
                          : '-'}
                      </TableCell>
                      <TableCell>{formatDate(guide.created_at)}</TableCell>
                      <TableCell>{formatDate(guide.updated_at)}</TableCell>
                    </TableRow>
                  ))}
                {filteredGuides.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={6} align="center">
                      No production guides found
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
          <TablePagination
            rowsPerPageOptions={[5, 10, 25]}
            component="div"
            count={filteredGuides.length}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
          />
        </Paper>
      )}
    </Box>
  );
};

export default ProductionGuides;
